import { apiClient } from './client';
import type {
  V2DeploymentInfo,
  V2DeployResponse,
  V2MigrateResponse,
  V2StatusResponse,
  V2SetupProgress,
  V2ApiStatusResponse,
  V2DeployRequest,
  V2MigrateRequest,
  V2StatusRequest,
  V2Director,
  CreateV2DirectorRequest,
} from '@/types/v2-deployment';

/**
 * Check V2 API availability
 */
export async function checkV2ApiStatus(): Promise<V2ApiStatusResponse> {
  const response = await apiClient.get<V2ApiStatusResponse>('/api/schools/v2-status');
  return response.data;
}

/**
 * Deploy a school to V2
 */
export async function deployV2(
  schoolCode: string,
  data?: V2DeployRequest
): Promise<V2DeployResponse> {
  const response = await apiClient.post<V2DeployResponse>(
    `/api/schools/${schoolCode}/deploy-v2`,
    data || {}
  );
  return response.data;
}

/**
 * Get V2 deployment progress for a school
 */
export async function getV2Progress(schoolCode: string): Promise<V2DeploymentInfo> {
  const response = await apiClient.get<V2DeploymentInfo>(
    `/api/schools/${schoolCode}/v2-progress`
  );
  return response.data;
}

/**
 * Restart V2 setup for a school
 */
export async function restartV2Setup(
  schoolCode: string
): Promise<{ message: string; progress: V2SetupProgress }> {
  const response = await apiClient.post<{ message: string; progress: V2SetupProgress }>(
    `/api/schools/${schoolCode}/v2-restart`
  );
  return response.data;
}

/**
 * Reset V2 deployment for a school (clear v2_tenant_id to allow redeployment)
 */
export async function resetV2Deployment(
  schoolCode: string
): Promise<{ message: string; oldTenantId: string; oldTenantGroupId: string }> {
  const response = await apiClient.post<{ message: string; oldTenantId: string; oldTenantGroupId: string }>(
    `/api/schools/${schoolCode}/v2-reset`
  );
  return response.data;
}

/**
 * Wipe V2 tenant content and replay the full setup, keeping the tenant
 * (same UUID, database, postgres role). Restarts a V1→V2 migration from
 * scratch without redeploying.
 */
export async function wipeV2Content(
  schoolCode: string
): Promise<{ message: string; tenantId: string }> {
  const response = await apiClient.post<{ message: string; tenantId: string }>(
    `/api/schools/${schoolCode}/v2-wipe`
  );
  return response.data;
}

/**
 * Migrate a V2 school to another tenant group
 */
export async function migrateV2(
  schoolCode: string,
  data: V2MigrateRequest
): Promise<V2MigrateResponse> {
  const response = await apiClient.post<V2MigrateResponse>(
    `/api/schools/${schoolCode}/v2-migrate`,
    data
  );
  return response.data;
}

/**
 * Change V2 tenant status
 */
export async function changeV2Status(
  schoolCode: string,
  data: V2StatusRequest
): Promise<V2StatusResponse> {
  const response = await apiClient.put<V2StatusResponse>(
    `/api/schools/${schoolCode}/v2-status`,
    data
  );
  return response.data;
}

/**
 * Get V2 tenant details for a school
 */
export async function getV2Tenant(schoolCode: string): Promise<V2DeploymentInfo> {
  const response = await apiClient.get<V2DeploymentInfo>(
    `/api/schools/${schoolCode}/v2-tenant`
  );
  return response.data;
}

/**
 * List V2 directors for a school
 */
export async function getV2Directors(schoolCode: string): Promise<V2Director[]> {
  const response = await apiClient.get<V2Director[]>(
    `/api/schools/${schoolCode}/v2-directors`
  );
  return response.data;
}

/**
 * Create a V2 director for a school
 */
export async function createV2Director(
  schoolCode: string,
  data: CreateV2DirectorRequest
): Promise<V2Director> {
  const response = await apiClient.post<V2Director>(
    `/api/schools/${schoolCode}/v2-directors`,
    data
  );
  return response.data;
}

/**
 * Delete a V2 director for a school
 */
export async function deleteV2Director(
  schoolCode: string,
  schoolUserId: string
): Promise<void> {
  await apiClient.delete(`/api/schools/${schoolCode}/v2-directors/${schoolUserId}`);
}
